
import { Component, Mixins } from 'vue-property-decorator';
import { RouteMixin } from '@/mixins';
import { IdentityVerificationHeader } from '@/components';
import { getFailureOptions } from '@sageworks/dynamic-forms/src/utils/toastr-utils';
import { UrlUtils } from '@/utils';
import { AddPersonaSnippetScript } from '@/utils/persona-utils';

@Component({
	components: { IdentityVerificationHeader }
})
export default class IdentityProtection extends Mixins(RouteMixin) {
	institutionName = '';
	newAccount = true;
	isLoading: boolean = false;
	submitting: boolean = false;
	rememberThisDevice: boolean = false;
	inquiryId = '';

	async created() {
		this.newAccount = this.directStore.state.Authentication.newAccount;
		if (this.newAccount) {
			this.institutionName = (await UrlUtils.getOnlinePortalInstitutionFromUrl())?.institutionName ?? '';
		}

		if (!this.personaSettings) {
			this.isLoading = true;
			await this.directStore.dispatch.Persona.fetchPersonaSettings();
			this.isLoading = false;
		}

		// Check if there's a pending inquiry after fetching persona settings
		if (this.personaEnabled) {
			this.isLoading = true;
			await this.directStore.dispatch.Persona.checkPendingInquiry();
			this.isLoading = false;
		}
	}

	get personaSettings() {
		return this.directStore.state.Persona.settings;
	}

	get personaEnabled() {
		return this.personaSettings?.isEnabled ?? false;
	}

	get hasPendingInquiry() {
		return this.directStore.state.Persona.hasPendingInquiry ?? false;
	}

	get identityMessage() {
		if (this.personaEnabled) {
			return 'In order to protect your identity you will be guided through an identity verification process with our partner Persona.';
		}
		return "In order to protect your identity, you'll now be asked to enter information about yourself and answer a few questions.";
	}

	async continueVerification() {
		if (this.personaEnabled) {
			await this.openPersonaKYC();
		} else {
			await this.$router.push(this.OnlinePortalRoutes.VerifyIdentity.path);
		}
	}

	async openPersonaKYC(): Promise<void> {
		this.submitting = true;
		await AddPersonaSnippetScript();

		if (!this.personaSettings?.keys) {
			this.showErrorMessage();
			this.submitting = false;
			return;
		}

		const keys = this.personaSettings.keys;
		const templateId = keys.useSandboxApi ? keys.sandboxTemplateIdExternal : keys.prodTemplateIdExternal;
		const environmentId = keys.useSandboxApi ? keys.sandboxEnvironmentIdExternal : keys.prodEnvironmentIdExternal;
		const userId = this.directStore.state.Authentication.onlinePortalUserToIdVerify;
		// eslint-disable-next-line
		const client = new Persona.Client({
			templateId: templateId,
			environmentId: environmentId,
			referenceId: userId,
			onReady: () => client.open(),
			onComplete: async ({ inquiryId, status }: { inquiryId: string; status: string }) => {
				await this.handlePersonaComplete(inquiryId, status);
			},
			onCancel: () => {
				this.submitting = false;
			},
			onError: () => {
				this.showErrorMessage();
				this.submitting = false;
			},
			onEvent: async (eventName: string, metadata: any) => {
				await this.processPersonaEvent(eventName, metadata);
			}
		});
	}

	async handlePersonaComplete(inquiryId: string, status: string): Promise<void> {
		if (status === 'failed') {
			await this.saveFailedPersonaVerification();
			this.showErrorMessage();
			this.submitting = false;
			return;
		}

		const result = await this.performPersonaVerification(inquiryId);

		if (result && result.identityVerified) {
			// Scenario A: Verified - proceed with login
			await this.loginAndRedirect();
		} else if (result && result.isInProgress) {
			// Scenario B: In Progress - show message
			this.directStore.commit.Persona.SET_HAS_PENDING_INQUIRY(true);
			this.submitting = false;
		} else {
			// Scenario C: Failed - show error
			this.showErrorMessage();
			this.submitting = false;
		}
	}

	async performPersonaVerification(inquiryId: string): Promise<any> {
		try {
			const result = await this.directStore.dispatch.Persona.generatePersonaIdentityToken({
				inquiryId: inquiryId,
				rememberThisDevice: this.rememberThisDevice
			});
			return result;
		} catch {
			return null;
		}
	}

	async processPersonaEvent(eventName: string, metadata: any) {
		if (eventName === 'start') {
			this.inquiryId = metadata.inquiryId;
		} else if (eventName === 'page-change' && metadata) {
			// if metadata.name contains 'retry' it means that a verification failed
			if (metadata.name && metadata.name.includes('retry')) {
				await this.saveFailedPersonaVerification();
			}
		}
	}

	async saveFailedPersonaVerification(): Promise<void> {
		await this.directStore.dispatch.Persona.saveFailedPersonaVerification({ inquiryId: this.inquiryId });
	}

	showErrorMessage(): void {
		this.$root.$bvToast.toast('We could not verify your identity. Please try again.', getFailureOptions());
	}

	async loginAndRedirect(): Promise<void> {
		await this.directStore.dispatch.Authentication.login({
			username: this.directStore.state.Login.userEmail,
			secret: this.directStore.state.Login.userPassword
		});
		await this.$router.push(this.OnlinePortalRoutes.Home.path);
	}
}
